import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Paper, Stack, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  useTheme,
} from '@mui/material';
import { ReportLayout, exportToCsv, useReportDates } from './ReportLayout';
import { reportsApi } from '../../api/reports';

// ─── Types ────────────────────────────────────────────────────────────────────
interface JobWorkRow {
  id: number;
  job_work_code: string;
  design_no: string;
  vendor_id: number | null;
  vendor: string;
  job_type: string;
  issue_date: string;
  total_issued: number;
  total_received: number;
  total_defects: number;
  pending_qty: number;
  aging_days: number;
  aging_bucket: string;
  status: string;
}

interface VendorGroup {
  vendor_id: number | null;
  vendor: string;
  rows: JobWorkRow[];
  total_issued: number;
  total_received: number;
  total_defects: number;
  total_pending: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function agingChip(bucket: string) {
  const color =
    bucket === '30+ days'   ? 'error'   :
    bucket === '16-30 days' ? 'warning' :
    bucket === '8-15 days'  ? 'info'    : 'success';
  return <Chip label={bucket} color={color} size="small" sx={{ fontSize: '0.68rem', height: 20 }} />;
}

function statusChip(status: string) {
  const color = status === 'PENDING' ? 'warning' : status === 'PARTIAL' ? 'info' : 'default';
  return <Chip label={status} color={color as any} size="small" variant="outlined" sx={{ fontSize: '0.68rem', height: 20 }} />;
}

function groupByVendor(rows: JobWorkRow[]): VendorGroup[] {
  const map = new Map<string, VendorGroup>();
  for (const row of rows) {
    const key = String(row.vendor_id ?? '__no_vendor__');
    if (!map.has(key)) {
      map.set(key, {
        vendor_id: row.vendor_id,
        vendor: row.vendor || '(No Vendor)',
        rows: [],
        total_issued: 0,
        total_received: 0,
        total_defects: 0,
        total_pending: 0,
      });
    }
    const g = map.get(key)!;
    g.rows.push(row);
    g.total_issued   += row.total_issued;
    g.total_received += row.total_received;
    g.total_defects  += row.total_defects;
    g.total_pending  += row.pending_qty;
  }
  // Sort groups by total pending desc
  return [...map.values()].sort((a, b) => b.total_pending - a.total_pending);
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function JobWorkAgeingReport() {
  const { startDate, endDate, setStartDate, setEndDate } = useReportDates();
  const theme = useTheme();
  const [data, setData] = useState<JobWorkRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportsApi.getJobWorkAgeing({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      });
      setData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [startDate, endDate]); // eslint-disable-line

  const groups = groupByVendor(data);
  const grandTotal = {
    issued:   data.reduce((s, r) => s + r.total_issued, 0),
    received: data.reduce((s, r) => s + r.total_received, 0),
    defects:  data.reduce((s, r) => s + r.total_defects, 0),
    pending:  data.reduce((s, r) => s + r.pending_qty, 0),
  };

  const divider    = theme.palette.divider;
  const thickBorder = `2px solid ${divider}`;
  const thinBorder  = `1px solid ${divider}`;

  const hdCell = { fontWeight: 700, py: 1 };

  return (
    <ReportLayout
      title="Job Work Ageing — Pending"
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      onExport={() => exportToCsv('job-work-ageing.csv', data.map((r) => ({
        vendor:        r.vendor,
        job_work_code: r.job_work_code,
        design_no:     r.design_no,
        job_type:      r.job_type,
        issue_date:    r.issue_date ? new Date(r.issue_date).toLocaleDateString('en-IN') : '',
        issued:        r.total_issued,
        received:      r.total_received,
        defects:       r.total_defects,
        pending:       r.pending_qty,
        aging_days:    r.aging_days,
        aging_bucket:  r.aging_bucket,
        status:        r.status,
      })))}
      loading={loading}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : data.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No pending job works found.
        </Typography>
      ) : (
        <>
          {/* ── Summary chips ─────────────────────────────────────── */}
          <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Chip label={`${data.length} Job Works`} variant="outlined" />
            <Chip label={`${groups.length} Vendors`} variant="outlined" />
            <Chip label={`Issued: ${grandTotal.issued}`} color="info" variant="outlined" />
            <Chip label={`Received: ${grandTotal.received}`} color="success" variant="outlined" />
            {grandTotal.defects > 0 && <Chip label={`Defects: ${grandTotal.defects}`} color="error" variant="outlined" />}
            <Chip label={`Pending: ${grandTotal.pending}`} color="warning" />
          </Stack>

          {/* ── Grouped table ─────────────────────────────────────── */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" sx={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ ...hdCell, width: 140 }}>JW Code</TableCell>
                  <TableCell sx={{ ...hdCell, width: 120 }}>Design No</TableCell>
                  <TableCell sx={{ ...hdCell, width: 110 }}>Job Type</TableCell>
                  <TableCell sx={{ ...hdCell, width: 100 }}>Issue Date</TableCell>
                  <TableCell align="right" sx={{ ...hdCell, width: 80 }}>Issued</TableCell>
                  <TableCell align="right" sx={{ ...hdCell, width: 85 }}>Received</TableCell>
                  <TableCell align="right" sx={{ ...hdCell, width: 75 }}>Defects</TableCell>
                  <TableCell align="right" sx={{ ...hdCell, width: 80 }}>Pending</TableCell>
                  <TableCell sx={{ ...hdCell, width: 110 }}>Aging</TableCell>
                  <TableCell sx={{ ...hdCell, width: 90 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((group, gi) => {
                  const isLastGroup = gi === groups.length - 1;
                  return (
                    <React.Fragment key={group.vendor_id ?? 'no-vendor'}>
                      {/* ── Vendor header row ── */}
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell
                          colSpan={10}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            color: 'primary.main',
                            borderTop: thickBorder,
                            borderBottom: thinBorder,
                            py: 0.75,
                            pl: 1.5,
                          }}
                        >
                          {group.vendor}
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                            ({group.rows.length} job work{group.rows.length !== 1 ? 's' : ''})
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* ── Individual job work rows ── */}
                      {group.rows.map((row, ri) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            bgcolor: 'background.paper',
                            '& td': {
                              borderBottom: ri === group.rows.length - 1 ? 'none' : thinBorder,
                              py: 0.75,
                            },
                          }}
                        >
                          <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>{row.job_work_code}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{row.design_no}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{row.job_type}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>
                            {row.issue_date ? new Date(row.issue_date).toLocaleDateString('en-IN') : '—'}
                          </TableCell>
                          <TableCell align="right"><Typography variant="body2">{row.total_issued}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2">{row.total_received}</Typography></TableCell>
                          <TableCell align="right">
                            {row.total_defects > 0
                              ? <Chip label={row.total_defects} color="error" size="small" variant="outlined" sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem' }} />
                              : <Typography variant="body2" color="text.disabled">—</Typography>}
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={row.pending_qty} color="warning" size="small" variant="filled" sx={{ fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
                          </TableCell>
                          <TableCell>{agingChip(row.aging_bucket)}</TableCell>
                          <TableCell>{statusChip(row.status)}</TableCell>
                        </TableRow>
                      ))}

                      {/* ── Vendor subtotal row ── */}
                      <TableRow
                        sx={{
                          bgcolor: 'action.selected',
                          '& td': { borderTop: thinBorder, borderBottom: isLastGroup ? 'none' : thickBorder, py: 0.75 },
                        }}
                      >
                        <TableCell colSpan={4} sx={{ fontWeight: 700, fontSize: '0.78rem', color: 'text.secondary', pl: 1.5 }}>
                          Subtotal — {group.vendor}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{group.total_issued}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{group.total_received}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {group.total_defects > 0
                            ? <Chip label={group.total_defects} color="error" size="small" variant="outlined" sx={{ fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
                            : <Typography variant="body2" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={group.total_pending} color="warning" size="small" variant="filled" sx={{ fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell colSpan={2} />
                      </TableRow>
                    </React.Fragment>
                  );
                })}

                {/* ── Grand total row ── */}
                <TableRow sx={{ bgcolor: 'grey.100', '& td': { borderTop: thickBorder, py: 1 } }}>
                  <TableCell colSpan={4} sx={{ fontWeight: 800, fontSize: '0.82rem', pl: 1.5 }}>
                    Grand Total
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{grandTotal.issued}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{grandTotal.received}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    {grandTotal.defects > 0
                      ? <Chip label={grandTotal.defects} color="error" size="small" variant="outlined" sx={{ fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
                      : <Typography variant="body2" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell align="right">
                    <Chip label={grandTotal.pending} color="warning" size="small" variant="filled" sx={{ fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </ReportLayout>
  );
}
